import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiResult } from '@/infra/api'
import { settingsClient } from './client'
import { SETTINGS_QUERY_KEYS } from './queryKeys'
import type {
  ApiKeyListItem,
  ApiKeyListParams,
  ApiKeySortOrder,
  ApiKeyStatus,
  BulkActionResponse,
  DeleteHostsRequest,
  HostListItem,
  HostListParams,
  HostSortOrder,
  PagedResponse,
  RevokeApiKeysRequest,
} from './types'

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 20

interface UseApiKeyListQueryParams {
  readonly page: number
  readonly size?: number
  readonly enabled: boolean
  readonly status?: ApiKeyStatus
  readonly environment?: readonly string[]
  readonly sort?: ApiKeySortOrder
}

interface UseApiKeyListQueryReturn {
  readonly data: PagedResponse<ApiKeyListItem> | null
  readonly isLoading: boolean
  readonly isError: boolean
  readonly error: string | null
  readonly refetch: () => void
}

export function useApiKeyListQuery({
  page,
  size = DEFAULT_PAGE_SIZE,
  enabled,
  status,
  environment,
  sort,
}: UseApiKeyListQueryParams): UseApiKeyListQueryReturn {
  const params = buildApiKeyQueryParams(page, size, status, environment, sort)

  const query = useQuery({
    queryKey: SETTINGS_QUERY_KEYS.apiKeyList(params),
    queryFn: async () => {
      const result = await settingsClient.listApiKeys(params)
      if (!result.success) throw new Error(result.message)
      return result.data
    },
    enabled,
    placeholderData: keepPreviousData,
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error?.message ?? null,
    refetch: () => {
      void query.refetch()
    },
  }
}

interface UseHostListPaginatedQueryParams {
  readonly page: number
  readonly size?: number
  readonly enabled: boolean
  readonly sort?: HostSortOrder
}

interface UseHostListPaginatedQueryReturn {
  readonly data: PagedResponse<HostListItem> | null
  readonly isLoading: boolean
  readonly isError: boolean
  readonly error: string | null
  readonly refetch: () => void
}

export function useHostListPaginatedQuery({
  page,
  size = DEFAULT_PAGE_SIZE,
  enabled,
  sort,
}: UseHostListPaginatedQueryParams): UseHostListPaginatedQueryReturn {
  const params = buildHostQueryParams(page, size, sort)

  const query = useQuery({
    queryKey: SETTINGS_QUERY_KEYS.hostList(params),
    queryFn: async () => {
      const result = await settingsClient.listHostsPaginated(params)
      if (!result.success) throw new Error(result.message)
      return result.data
    },
    enabled,
    placeholderData: keepPreviousData,
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error?.message ?? null,
    refetch: () => {
      void query.refetch()
    },
  }
}

// ---------------------------------------------------------------------------
// Private: Param builders (handle exactOptionalPropertyTypes)
// ---------------------------------------------------------------------------

function buildApiKeyQueryParams(
  page: number,
  size: number,
  status: ApiKeyStatus | undefined,
  environment: readonly string[] | undefined,
  sort: ApiKeySortOrder | undefined,
): ApiKeyListParams {
  const params: {
    page: number
    size: number
    status?: ApiKeyStatus
    environment?: readonly string[]
    sort?: ApiKeySortOrder
  } = { page, size }
  if (status) params.status = status
  if (environment) params.environment = environment
  if (sort) params.sort = sort
  return params
}

function buildHostQueryParams(
  page: number,
  size: number,
  sort: HostSortOrder | undefined,
): HostListParams {
  const params: { page: number; size: number; sort?: HostSortOrder } = { page, size }
  if (sort) params.sort = sort
  return params
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

interface UseRevokeApiKeysReturn {
  readonly revoke: (request: RevokeApiKeysRequest) => void
  readonly revokeAsync: (request: RevokeApiKeysRequest) => Promise<ApiResult<BulkActionResponse>>
  readonly isRevoking: boolean
  readonly error: string | null
  readonly result: BulkActionResponse | null
  readonly reset: () => void
}

export function useRevokeApiKeys(): UseRevokeApiKeysReturn {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: settingsClient.revokeApiKeys,
    onSuccess: (result) => {
      if (result.success) {
        void queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEYS.apiKeys })
      }
    },
  })

  return {
    revoke: mutation.mutate,
    revokeAsync: mutation.mutateAsync,
    isRevoking: mutation.isPending,
    error: mutation.data?.success === false ? mutation.data.message : null,
    result: mutation.data?.success ? mutation.data.data : null,
    reset: mutation.reset,
  }
}

interface UseDeleteHostsReturn {
  readonly deleteHosts: (request: DeleteHostsRequest) => void
  readonly deleteHostsAsync: (request: DeleteHostsRequest) => Promise<ApiResult<BulkActionResponse>>
  readonly isDeleting: boolean
  readonly error: string | null
  readonly result: BulkActionResponse | null
  readonly reset: () => void
}

export function useDeleteHosts(): UseDeleteHostsReturn {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: settingsClient.deleteHosts,
    onSuccess: (result) => {
      if (result.success) {
        void queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEYS.hosts })
      }
    },
  })

  return {
    deleteHosts: mutation.mutate,
    deleteHostsAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.data?.success === false ? mutation.data.message : null,
    result: mutation.data?.success ? mutation.data.data : null,
    reset: mutation.reset,
  }
}
